// SPDX-License-Identifier: GPL-3.0-or-later
// Shared helpers for the code-built characters (THEME.md section 5).
// Rules for every model: three.js primitives only, MeshStandardMaterial or MeshBasicMaterial,
// white edges with EdgesGeometry, labels with CanvasTexture, one Group per model, the group
// origin at the base center. Idle motion uses sin(t) and goes to 0 when reduced motion is on.

import {
  BackSide,
  BoxGeometry,
  type BufferGeometry,
  CanvasTexture,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  type Object3D,
  PlaneGeometry,
  Quaternion,
  SphereGeometry,
  SRGBColorSpace,
  Vector3,
} from "three";
import { type CanvasLike, type CreateCanvas, type Ctx2DLike, context2d } from "../art/canvas.ts";

/** What a character needs from the page. */
export interface CharacterEnv {
  /** Make a canvas for a label. A test gives a fake canvas. */
  readonly createCanvas: CreateCanvas;
  /** True when reduced motion is on. Read at each frame, so a toggle takes effect at once. */
  readonly reducedMotion: () => boolean;
}

/** One built character: the group and its two update functions. */
export interface Built {
  readonly group: Group;
  /** Set the pose for a game time (ms). Call it each frame. */
  tick(timeMs: number): void;
  /** Start an animation (see CHARACTER_ANIMATIONS). An unknown name does nothing. */
  play(name: string, arg: string, timeMs: number): void;
  /** Free the geometry, the materials and the textures of this character. */
  dispose(): void;
}

/** Key in `Object3D.userData` that holds the Built of a character group. */
export const BUILT_KEY = "pinballBuilt";

/** Get the Built of a group. Return null when the object is not a character. */
export function builtOf(obj: Object3D): Built | null {
  const value: unknown = obj.userData[BUILT_KEY];
  return value !== undefined && value !== null && typeof value === "object"
    ? (value as Built)
    : null;
}

/** A solid material of one color. */
export function solid(
  color: number,
  opts: {
    opacity?: number;
    wireframe?: boolean;
    side?: 0 | 1 | 2;
    emissive?: number;
    emissiveIntensity?: number;
  } = {},
): MeshStandardMaterial {
  const m = new MeshStandardMaterial({ color, roughness: 0.6, metalness: 0.1 });
  if (opts.opacity !== undefined && opts.opacity < 1) {
    m.transparent = true;
    m.opacity = opts.opacity;
  }
  if (opts.wireframe) m.wireframe = true;
  if (opts.side !== undefined) m.side = opts.side;
  if (opts.emissive !== undefined) m.emissive.setHex(opts.emissive);
  if (opts.emissiveIntensity !== undefined) m.emissiveIntensity = opts.emissiveIntensity;
  return m;
}

/** A wireframe material (white lines). It does not react to light. */
export function wire(color: number, opacity = 1): MeshBasicMaterial {
  const m = new MeshBasicMaterial({ color, wireframe: true });
  if (opacity < 1) {
    m.transparent = true;
    m.opacity = opacity;
  }
  return m;
}

/** Make a mesh at a position. */
export function put<G extends BufferGeometry, M extends MeshStandardMaterial | MeshBasicMaterial>(
  geometry: G,
  material: M,
  x = 0,
  y = 0,
  z = 0,
): Mesh<G, M> {
  const mesh = new Mesh(geometry, material);
  mesh.position.set(x, y, z);
  return mesh;
}

/** A pair of eyes: a white ball with a black pupil on its front. `y` and `z` are the centers. */
export function addEyes(parent: Object3D, gap: number, y: number, z: number, radius: number): void {
  for (const side of [-1, 1]) {
    parent.add(put(new SphereGeometry(radius, 12, 12), solid(0xffffff), side * gap, y, z));
    parent.add(
      put(
        new SphereGeometry(radius * 0.5, 8, 8),
        solid(0x000000),
        side * gap,
        y,
        z + radius * 0.85,
      ),
    );
  }
}

/** A pair of white brows. A positive `tilt` (radians) lowers the inner ends (an angry face). */
export function addBrows(
  parent: Object3D,
  gap: number,
  y: number,
  z: number,
  width: number,
  tilt: number,
): void {
  for (const side of [-1, 1]) {
    const brow = put(new BoxGeometry(width, width * 0.22, 3), solid(0xffffff), side * gap, y, z);
    brow.rotation.z = side * tilt;
    parent.add(brow);
  }
}

/** Add white edge lines (EdgesGeometry) as a child of the mesh. */
export function withEdges(mesh: Mesh, color = 0xffffff, thresholdDeg = 30): Mesh {
  const lines = new LineSegments(
    new EdgesGeometry(mesh.geometry, thresholdDeg),
    new LineBasicMaterial({ color }),
  );
  mesh.add(lines);
  return mesh;
}

/** `Object3D.userData` flag of an outline hull. Test helpers skip these meshes. */
export const OUTLINE_KEY = "pinballOutline";

/** Thickness of an outline in units, before the character scale. */
const OUTLINE_MAX = 2.4;
const OUTLINE_MIN = 0.7;
/** The outline is this part of the smallest side of the part, within the limits above. */
const OUTLINE_RATIO = 0.14;

/**
 * Add a white outline to a part: a slightly larger copy that draws only its back faces. The
 * part keeps its recipe color. The outline gives a clear silhouette on the black field at a
 * small size. Do not use it on a translucent part, a wireframe or a flat label.
 */
export function outlined<M extends Mesh>(mesh: M, color = 0xffffff): M {
  const geometry = mesh.geometry;
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) return mesh;
  const size = box.getSize(new Vector3());
  const smallest = Math.min(...[size.x, size.y, size.z].filter((v) => v > 0.001));
  if (!Number.isFinite(smallest)) return mesh;
  const t = Math.min(OUTLINE_MAX, Math.max(OUTLINE_MIN, smallest * OUTLINE_RATIO));
  const hull = new Mesh(geometry, new MeshBasicMaterial({ color, side: BackSide }));
  // Grow by `t` on each side. A flat axis does not grow.
  const grow = (v: number): number => (v > 0.001 ? 1 + (2 * t) / v : 1);
  hull.scale.set(grow(size.x), grow(size.y), grow(size.z));
  // Keep the hull centered on the part, also when the geometry is not centered on its origin.
  const center = box.getCenter(new Vector3());
  hull.position.set(
    center.x * (1 - hull.scale.x),
    center.y * (1 - hull.scale.y),
    center.z * (1 - hull.scale.z),
  );
  hull.userData[OUTLINE_KEY] = true;
  hull.name = `${mesh.name}Outline`;
  mesh.add(hull);
  return mesh;
}

/** How a character stands on the table. */
export interface Stance {
  /** Uniform scale. The table is seen from far away, so a character is larger than its recipe. */
  readonly scale: number;
  /** Lean back from the vertical, in degrees. The front then faces the camera. */
  readonly leanDeg: number;
  /**
   * How much of the yaw of the mount (from the table data) the character keeps. 1 (default) keeps
   * all of it. A character that the data turns sideways gets a smaller value, so that its label
   * still faces the camera. The sign of the turn is kept.
   */
  readonly turn?: number;
}

const AXIS_X = new Vector3(1, 0, 0);
const AXIS_Y = new Vector3(0, 1, 0);
const qYaw = new Quaternion();
const qLean = new Quaternion();
const qFace = new Quaternion();

/**
 * Set the pose of the character inside its mount. The world pose is: turn about the vertical
 * axis, then lean back about the world x axis (away from the camera). The mount yaw comes from
 * the table data. The lean must not turn with that yaw, or a character that faces sideways would
 * lie on its side.
 */
export function orient(mount: Object3D, stance: Stance): void {
  const inner = mount.children[0];
  if (!inner) return;
  const yaw = mount.rotation.y;
  qYaw.setFromAxisAngle(AXIS_Y, -yaw);
  qLean.setFromAxisAngle(AXIS_X, -rad(stance.leanDeg));
  qFace.setFromAxisAngle(AXIS_Y, yaw * (stance.turn ?? 1));
  inner.quaternion.copy(qYaw).multiply(qLean).multiply(qFace);
}

/**
 * Put a built character on a mount. The mount is the object that the scene places and turns.
 * Inside it the character leans back and is scaled, so a label on its front faces the camera.
 * The Built stays attached to the mount, so `builtOf(mount)` works. Call `orient` each frame,
 * because the scene sets the yaw of the mount after the build.
 */
export function mountBuilt(built: Built, stance: Stance): Group {
  const mount = new Group();
  mount.name = `${built.group.name}Mount`;
  built.group.scale.setScalar(stance.scale);
  mount.add(built.group);
  mount.userData[BUILT_KEY] = built;
  orient(mount, stance);
  return mount;
}

/** A plane with a canvas texture. */
export interface Label {
  readonly mesh: Mesh<PlaneGeometry, MeshBasicMaterial>;
  /** Draw again and mark the texture as changed. */
  redraw(draw: (ctx: Ctx2DLike, w: number, h: number) => void): void;
}

/** Make a label plane `w` x `h` units with a texture of `texW` x `texH` pixels. */
export function makeLabel(
  env: CharacterEnv,
  w: number,
  h: number,
  texW: number,
  texH: number,
  draw: (ctx: Ctx2DLike, w: number, h: number) => void,
): Label {
  const canvas: CanvasLike = env.createCanvas(texW, texH);
  const ctx = context2d(canvas);
  draw(ctx, texW, texH);
  const texture = new CanvasTexture(canvas as unknown as HTMLCanvasElement);
  texture.colorSpace = SRGBColorSpace;
  const mesh = new Mesh(new PlaneGeometry(w, h), new MeshBasicMaterial({ map: texture }));
  return {
    mesh,
    redraw(next) {
      next(ctx, texW, texH);
      texture.needsUpdate = true;
    },
  };
}

/** Free every geometry, material and texture under a group. */
export function disposeTree(root: Object3D): void {
  root.traverse((obj) => {
    const mesh = obj as Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    const mat = mesh.material;
    const list = Array.isArray(mat) ? mat : mat ? [mat] : [];
    for (const m of list) {
      const map = (m as MeshBasicMaterial).map;
      if (map) map.dispose();
      m.dispose();
    }
  });
}

/** Degrees to radians. */
export const rad = (deg: number): number => (deg * Math.PI) / 180;

/** Seconds from a game time in ms. */
export const sec = (timeMs: number): number => timeMs / 1000;

/** 1 when motion is on, 0 when reduced motion is on. */
export const motion = (env: CharacterEnv): number => (env.reducedMotion() ? 0 : 1);

/** A new group with a name. */
export function namedGroup(name: string): Group {
  const g = new Group();
  g.name = name;
  return g;
}
